import React, { useEffect, useRef, useState } from 'react';
import { beHttp } from '../hooks/api/beHttp';

/** Panel de administración (Bubble). Fallback cuando no hay acción posible. */
const ADMIN_PANEL_URL = 'https://admin.psimammoliti.com';

type ActionStatus = 'loading' | 'success' | 'error' | 'invalid';

interface CertActionResult {
  ok: boolean;
  action: 'approve' | 'reject';
  service: string;
}

/**
 * /cert-action — Página pública que ejecuta la aprobación o el rechazo de un certificado
 * desde el email de notificación al equipo.
 *
 * Params esperados:
 *   action = 'approve' | 'reject'
 *   token  = token de un solo uso emitido por psiplatform-be al avisar del certificado
 *
 * Antes recibía `psyId`, `service` y `psyEmail` como query params sin firma: cualquiera
 * podía editar la URL y habilitar servicios de otra psicóloga (el endpoint que usaba se
 * autentica con la x-api-key, que viaja en el bundle público). Ahora sólo viaja el token
 * y todo lo demás lo resuelve el backend. El rechazo, además, antes tiraba 401 y no
 * hacía nada: apuntaba a `/v1/psi-platform/*`, que exige Bearer.
 */
export const CertActionPage: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const action = params.get('action') as 'approve' | 'reject' | null;
  const token = params.get('token') ?? '';

  const [status, setStatus] = useState<ActionStatus>('loading');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [service, setService] = useState<string>('');
  const executedRef = useRef(false);

  useEffect(() => {
    // Ejecutar solo una vez (StrictMode monta dos veces en dev). El backend igual es
    // idempotente: el token es de un solo uso.
    if (executedRef.current) return;
    executedRef.current = true;

    if (!action || !token || (action !== 'approve' && action !== 'reject')) {
      setStatus('invalid');
      return;
    }

    (async () => {
      try {
        const result = await beHttp.publicPost<CertActionResult>(
          `/api/cert-action/${action}`,
          { token },
        );
        setService(result?.service ?? '');
        setStatus('success');
      } catch (e) {
        setErrorMsg(e instanceof Error ? e.message : 'Error desconocido');
        setStatus('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isApprove = action === 'approve';

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600 font-medium text-sm">
            {isApprove ? 'Habilitando servicio…' : 'Procesando rechazo…'}
          </p>
        </div>
      </div>
    );
  }

  // ── Parámetros inválidos ──────────────────────────────────────────────────────
  if (status === 'invalid') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white rounded-2xl shadow-sm max-w-md w-full mx-4 overflow-hidden">
          <div className="bg-gray-500 px-8 py-7 text-center">
            <p className="text-white text-xl font-bold">⚠️ Link inválido</p>
          </div>
          <div className="p-8 text-center">
            <p className="text-gray-600 mb-6 text-sm leading-relaxed">
              El link que usaste no tiene los datos necesarios. Revisá el email original o
              realizá la acción manualmente.
            </p>
            <a
              href={ADMIN_PANEL_URL}
              className="inline-block px-6 py-3 bg-[#6D57BC] text-white font-semibold rounded-xl text-sm hover:bg-[#5a46a5] transition-colors"
            >
              Ir al panel de admin
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white rounded-2xl shadow-sm max-w-md w-full mx-4 overflow-hidden">
          <div className="bg-red-600 px-8 py-7 text-center">
            <p className="text-white text-xl font-bold">❌ No se pudo completar la acción</p>
          </div>
          <div className="p-8">
            <p className="text-gray-600 mb-4 text-sm leading-relaxed">
              {isApprove
                ? 'Ocurrió un error al habilitar el servicio.'
                : 'Ocurrió un error al procesar el rechazo.'}
            </p>
            {errorMsg && (
              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6 font-mono break-all">
                {errorMsg}
              </p>
            )}
            <p className="text-sm text-gray-500 mb-6">
              Si el link ya fue usado o venció, la acción se puede realizar manualmente
              desde el panel de administración.
            </p>
            <a
              href={ADMIN_PANEL_URL}
              className="inline-block px-6 py-3 bg-[#6D57BC] text-white font-semibold rounded-xl text-sm hover:bg-[#5a46a5] transition-colors"
            >
              Ir al panel de admin
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Éxito ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="bg-white rounded-2xl shadow-sm max-w-md w-full mx-4 overflow-hidden">
        <div className={`px-8 py-7 text-center ${isApprove ? 'bg-green-600' : 'bg-red-600'}`}>
          <p className="text-white text-xl font-bold">
            {isApprove ? '✅ Servicio habilitado' : '❌ Documentación rechazada'}
          </p>
        </div>
        <div className="p-8">
          {service && (
            <div className="bg-[#F5F3FF] border-l-4 border-[#7C3AED] rounded-r-xl px-5 py-4 mb-6">
              <p className="text-xs font-bold text-[#7C3AED] uppercase tracking-wider mb-1">
                Servicio
              </p>
              <p className="text-lg font-bold text-gray-900">{service}</p>
            </div>
          )}
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            {isApprove
              ? `El servicio${service ? ` "${service}"` : ''} fue habilitado exitosamente. La psicóloga podrá verlo activo la próxima vez que abra su perfil.`
              : 'La documentación fue rechazada y se le envió un email a la psicóloga para que suba nueva documentación.'}
          </p>
          <a
            href={ADMIN_PANEL_URL}
            className="inline-block px-6 py-3 bg-[#6D57BC] text-white font-semibold rounded-xl text-sm hover:bg-[#5a46a5] transition-colors"
          >
            Ir al panel de admin
          </a>
        </div>
      </div>
    </div>
  );
};
